/**
 * FreeDrawRoom: the M2 free-draw canvas + tools composition.
 *
 * Composes:
 *   - <Canvas>: high-DPI drawing surface
 *   - <Toolbar>: tool selector (brush/eraser/fill/undo/clear)
 *   - <SpectatorList>: connected players panel
 *   - <CursorOverlay>: custom cursor overlay (hides native cursor)
 *
 * This is the primary M2 deliverable: a working collaborative free-draw room.
 */

import { useRef } from "preact/hooks";
import { DrawingCanvas } from "../canvas/DrawingCanvas.js";
import { CursorOverlay } from "../canvas/CursorOverlay.js";
import { Toolbar } from "../tools/Toolbar.js";
import { SpectatorList } from "../components/SpectatorList.js";
import { ShareLinkCTA } from "../components/ShareLinkCTA.js";
import { EmptyRoomNudge } from "../components/EmptyRoomNudge.js";
import { useUndoClear } from "../canvas/useUndoClear.js";
import { clearStrokes, lastStrokeId, popStrokeById } from "../canvas/strokesStore.js";
import type { SerializedPlayer } from "@drawlol/shared";
import type { ClientControlFrame } from "../ws.js";

type FreeDrawRoomProps = {
  roomId: string;
  players: ReadonlyArray<SerializedPlayer>;
  creatorId: string | null;
  playerId: string | null;
  /**
   * M4 batch 7 commit 2: binary STROKE_* sender, forwarded from RoomShell.
   * Optional so unit tests can mount FreeDrawRoom without spinning up a
   * fake socket: the canvas still works locally without it.
   */
  sendBinary?: (buf: ArrayBuffer) => boolean;
  /**
   * M4 batch 7 commit 4: JSON control-frame sender. Used to ship
   * STROKE_UNDO and STROKE_CLEAR after the toolbar's local optimistic
   * update. Optional so tests can mount without a socket.
   */
  sendFrame?: (frame: ClientControlFrame) => boolean;
};

export function FreeDrawRoom({
  roomId,
  players,
  creatorId,
  playerId,
  sendBinary,
  sendFrame,
}: FreeDrawRoomProps) {
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const isCreator = creatorId !== null && playerId === creatorId;

  // M4 batch 7 commit 4: Toolbar undo/clear → strokes store + server.
  // Optimistic local update first, then dispatch the matching JSON
  // control frame. The server's broadcast back is a dedup no-op.
  // Non-creators see the toolbar without these handlers; the buttons
  // exist (visual parity) but a tap is a no-op (no callbacks bound).
  const { clearHolding, onClearStart, onClearEnd, onUndo } = useUndoClear({
    onUndo: () => {
      const id = lastStrokeId();
      if (!id) return;
      popStrokeById(id);
      sendFrame?.({ kind: "STROKE_UNDO" });
    },
    onClear: () => {
      clearStrokes();
      sendFrame?.({ kind: "STROKE_CLEAR" });
    },
  });

  return (
    <div class="drawing-room" data-room-id={roomId}>
      <div class="free-draw-header">
        <a
          href="/"
          class="free-draw-back-link"
          data-testid="free-draw-back-link"
          aria-label="Back to drawlol home"
        >
          ← back
        </a>
        <span class="free-draw-room-id" data-testid="free-draw-room-id">
          room: <code>{roomId}</code>
        </span>
        {/*
         * V4.1.T6-share (b): let the free-draw owner share from inside the
         * room. The share URL resolves at /r/<CODE> via RoomShell.
         *
         * Review P3: gate on players.length >= 2 so the EmptyRoomNudge below
         * is the sole share CTA while alone, otherwise both render at once,
         * duplicating the copy button, room code, and aria-labels. The header
         * CTA takes over once a second player joins. Mirrors LobbyShell.
         */}
        {players.length >= 2 && <ShareLinkCTA roomId={roomId} />}
      </div>

      {/* Empty-room nudge: surfaces the share CTA prominently while alone */}
      <EmptyRoomNudge roomId={roomId} playerCount={players.length} />

      {/* Main canvas area: top, centered */}
      <div
        class="canvas-wrapper"
        ref={canvasWrapperRef}
        style={{ cursor: isCreator ? "none" : "default" }}
      >
        {/*
         * Codex M4 review P2-19: only the creator can produce strokes in
         * a freeDraw room (server enforces `freeDraw.creatorId === playerId`).
         * Pass `readOnly` to non-creators so the canvas skips pointer
         * handlers entirely: without it, a non-creator's pointer events
         * append local-only strokes that the server rejects, leaving the
         * canvas visually diverged from the creator's.
         */}
        <DrawingCanvas
          readOnly={!isCreator}
          {...(isCreator && sendBinary ? { sendBinary } : {})}
          {...(isCreator && sendFrame
            ? {
                sendFill: (op) =>
                  sendFrame({
                    kind: "STROKE_FILL",
                    strokeId: op.strokeId,
                    point: op.point,
                    color: op.color,
                  }),
              }
            : {})}
        />
        {isCreator && <CursorOverlay targetRef={canvasWrapperRef} />}
      </div>

      {/* Tool row, horizontal: toolbar + players, below canvas */}
      <div class="tool-row">
        {isCreator ? (
          <Toolbar fillEnabled onUndo={onUndo} onClearStart={onClearStart} onClearEnd={onClearEnd} clearHolding={clearHolding} />
        ) : <p role="status">Only the creator can draw.</p>}
        <SpectatorList players={players} creatorId={creatorId} myId={playerId} />
      </div>
    </div>
  );
}
